import Dialog from "@mui/material/Dialog";
import { useContext, useEffect, useState } from "react";
import {
  EmptyImageDialog,
  ImageDialogContext,
} from "../context/ImageDialogContext";
import styles from "./ImageDialog.module.css";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { IconButton, Link, Theme, useMediaQuery } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { motion } from "motion/react";
import { fadeProps } from "../constants/transitionConstants";

/**
 * The artwork viewer (DESIGN.md: Artwork viewer): a near-black void, the photo shown whole,
 * and the artist and caption set below it rather than over it.
 */
export default function ImageDialog() {
  const [open, setOpen] = useState<boolean>(false);
  const [currentImage, setCurrentImage] = useState<number>(0);
  const { imageDialog, setImageDialog } = useContext(ImageDialogContext);
  const isPhone = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    setOpen(imageDialog.isOpen);
  }, [imageDialog]);

  const handleClose = () => {
    setOpen(imageDialog.isOpen);
    setCurrentImage(0);
    setImageDialog(EmptyImageDialog);
  };

  const changeImage = (direction: number, image: number) => {
    if (
      imageDialog.content.postChildren.length - 1 <
      currentImage + direction
    ) {
      setCurrentImage(0);
    } else if (0 > currentImage + direction) {
      setCurrentImage(imageDialog.content.postChildren.length - 1);
    } else {
      setCurrentImage(currentImage + direction);
    }
  };

  return (
    <Dialog
      component={motion.div}
      open={open}
      onClose={handleClose}
      fullWidth={false}
      maxWidth={false}
      fullScreen={isPhone}
      slotProps={{
        paper: {
          className: styles["paper"],
          "aria-label":
            imageDialog.content.caption || `post by ${imageDialog.content.username}`,
        },
        backdrop: { className: styles["backdrop"] },
      }}
      {...fadeProps}
    >
      {/* fullScreen removes the backdrop, so an explicit close control is the only way out on
          a phone. */}
      <IconButton
        aria-label="close"
        onClick={handleClose}
        className={styles["close"]}
      >
        <CloseRoundedIcon />
      </IconButton>
      <figure className={styles["figure"]}>
        <div className={styles["stage"]}>
          <img
            src={
              imageDialog.content.mediaType === "IMAGE"
                ? imageDialog.content.mediaUrl
                : imageDialog.content.postChildren.at(currentImage)?.mediaUrl
            }
            alt={
              imageDialog.content.caption ||
              `post by ${imageDialog.content.username}`
            }
            className={styles["image"]}
          />
          {imageDialog.content.mediaType !== "IMAGE" && (
            <div className={styles["overlay"]}>
              <button
                type="button"
                aria-label="previous image"
                className={styles["back"]}
                onClick={() => changeImage(-1, currentImage)}
              >
                <span className={styles["chip"]}>
                  <ArrowBackIosNewRoundedIcon />
                </span>
              </button>
              <button
                type="button"
                aria-label="next image"
                className={styles["forward"]}
                onClick={() => changeImage(1, currentImage)}
              >
                <span className={styles["chip"]}>
                  <ArrowForwardIosRoundedIcon />
                </span>
              </button>
            </div>
          )}
        </div>
        <figcaption className={styles["texts"]}>
          <Link
            href={`/user/${imageDialog.content.userId}`}
            underline="hover"
            className={styles["artist"]}
          >
            {imageDialog.content.username}
          </Link>
          {imageDialog.content.caption && <p>{imageDialog.content.caption}</p>}
        </figcaption>
      </figure>
    </Dialog>
  );
}
